import { supabase } from '../lib/supabase';
import { calculatePrice } from '../utils/pricingEngine';
import { normalizeCity } from '../utils/cityNormalization';
import {
  resolveEffectiveWorkloadMultiplier,
  resolveWorkloadMultiplier,
  resolveProductPhysicalSizeMultiplier,
} from '../utils/workloadPricing';
import { normalizeExperienceLevelToUi } from '../utils/experienceLevel';

/**
 * Smart Pricing Service
 * 
 * This service handles:
 * - Fetching pricing configuration from Supabase
 * - Wrapping the pure pricing engine with data extraction
 * - Caching pricing configuration
 * 
 * The actual price calculation is done by the pure pricing engine (pricingEngine.js)
 */

// Cache pricing config (refresh on demand)
let pricingConfigCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

// Realtime broadcast channel — مشترك دائم لإرسال/استقبال تغييرات التسعير
const _pricingSyncChannel = supabase.channel('pricing-sync');
_pricingSyncChannel.subscribe();

/**
 * Clear pricing cache - call after admin saves new pricing settings.
 * Uses BroadcastChannel (same browser) + Supabase broadcast (cross-device).
 */
export const clearPricingCache = () => {
  pricingConfigCache = null;
  cacheTimestamp = null;
  // Primary: BroadcastChannel API — reliable same-browser cross-tab notification
  try {
    const bc = new BroadcastChannel('pricing-cache-clear');
    bc.postMessage({ ts: Date.now() });
    bc.close();
  } catch (_) { /* unsupported env */ }
  // Secondary: Supabase broadcast — cross-device notification
  _pricingSyncChannel.send({
    type: 'broadcast',
    event: 'pricing_updated',
    payload: { ts: Date.now() },
  }).catch(() => {});
  console.log('🗑️ Pricing cache cleared + broadcast sent');
};

/**
 * Fetch all pricing configuration from Supabase
 */
export const getPricingConfig = async (forceRefresh = false) => {
  const now = Date.now();
  
  // Return cached config if still valid
  if (!forceRefresh && pricingConfigCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    return pricingConfigCache;
  }

  try {
    // Fetch pricing settings
    const { data: settings, error: settingsError } = await supabase
      .from('pricing_settings')
      .select('*')
      .eq('id', '00000000-0000-0000-0000-000000000001')
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') {
      console.error('Error fetching pricing settings:', settingsError);
    }

    // Fetch active multipliers
    const [
      cityMultipliers,
      experienceMultipliers,
      equipmentMultipliers,
      typeMultipliers,
      shootingMethodMultipliers,
      hoursMultipliers,
      workloadMultipliersResult,
      placeTypeMultipliersResult,
      deliverablesData,
    ] = await Promise.all([
      supabase
        .from('pricing_city_multipliers')
        .select('*')
        .eq('is_active', true),
      supabase
        .from('pricing_experience_multipliers')
        .select('*')
        .eq('is_active', true),
      supabase
        .from('pricing_equipment_multipliers')
        .select('*')
        .eq('is_active', true),
      supabase
        .from('pricing_photography_type_multipliers')
        .select('*')
        .eq('is_active', true),
      supabase
        .from('pricing_shooting_method_multipliers')
        .select('*')
        .eq('is_active', true),
      supabase
        .from('pricing_hours_multipliers')
        .select('*')
        .eq('is_active', true),
      supabase
        .from('pricing_workload_multipliers')
        .select('*')
        .eq('is_active', true),
      supabase
        .from('pricing_place_type_multipliers')
        .select('*')
        .eq('is_active', true),
      supabase
        .from('app_settings')
        .select('*')
        .eq('key', 'deliverables_settings')
    ]);

    if (workloadMultipliersResult?.error) {
      console.warn('⚠️ pricing_workload_multipliers:', workloadMultipliersResult.error.message);
    }

    if (placeTypeMultipliersResult?.error) {
      console.warn('⚠️ pricing_place_type_multipliers:', placeTypeMultipliersResult.error.message);
    }

    const placeTypeMultiplierByKey = {};
    (placeTypeMultipliersResult?.data || []).forEach((row) => {
      const m = parseFloat(row.multiplier);
      if (!Number.isNaN(m) && m > 0) {
        placeTypeMultiplierByKey[row.place_key] = { key: row.place_key, label: row.place_label, multiplier: m };
      }
    });

    const workloadMultiplierByBucket = {};
    const productPhysicalMultiplierByBucket = {};
    const workloadLabelByBucket = {};
    (workloadMultipliersResult?.data || []).forEach((row) => {
      const m = parseFloat(row.multiplier);
      if (Number.isNaN(m) || m <= 0) return;
      if (row.multiplier_kind === 'product_physical') {
        productPhysicalMultiplierByBucket[row.bucket_key] = m;
      } else {
        workloadMultiplierByBucket[row.bucket_key] = m;
        workloadLabelByBucket[row.bucket_key] = row.label_ar;
      }
    });

    // Parse deliverables settings from DB
    let deliverablesSettings = null;
    
    if (deliverablesData?.error) {
      console.error('❌ Error fetching deliverables_settings:', deliverablesData.error);
    }
    
    if (deliverablesData?.data && deliverablesData.data.length > 0 && deliverablesData.data[0]?.value) {
      try {
        deliverablesSettings = JSON.parse(deliverablesData.data[0].value);
        console.log('✅ Deliverables settings from DB:', {
          photoBasePrice: deliverablesSettings.photoBasePrice,
          videoBasePrice: deliverablesSettings.videoBasePrice,
          photoBulkDiscounts: deliverablesSettings.photoBulkDiscounts?.length,
          videoBulkDiscounts: deliverablesSettings.videoBulkDiscounts?.length,
        });
      } catch (e) {
        console.error('❌ Failed to parse deliverables settings JSON:', e);
      }
    } else {
      console.warn('⚠️ No deliverables_settings found in DB, data:', deliverablesData?.data);
    }
    
    // Use defaults ONLY if DB query failed
    if (!deliverablesSettings) {
      deliverablesSettings = {
        photoBasePrice: 20,
        videoBasePrice: 100,
        shortVideoBasePrice: 50,
        photoBulkDiscounts: [
          { minCount: 20, discount: 12 },
          { minCount: 50, discount: 15 },
        ],
        videoBulkDiscounts: [
          { minCount: 3, discount: 15 },
          { minCount: 5, discount: 40 },
        ],
        shortVideoBulkDiscounts: [],
      };
      console.warn('⚠️ Using DEFAULT deliverables settings (not from DB)');
    }

    // Build config object
    const config = {
      settings: settings || {
        base_hour_price: 200,
        vat_percent: 15,
        is_enabled: true
      },
      cityMultipliers: {},
      experienceMultipliers: {},
      equipmentMultipliers: {},
      typeMultipliers: {},
      shootingMethodMultipliers: {},
      hoursMultipliers: {},
      workloadMultiplierByBucket,
      productPhysicalMultiplierByBucket,
      workloadLabelByBucket,
      placeTypeMultiplierByKey,
      deliverablesSettings
    };

    // Map multipliers by key for quick lookup
    if (cityMultipliers.data) {
      cityMultipliers.data.forEach(m => {
        config.cityMultipliers[m.city_key] = {
          key: m.city_key,
          label: m.city_label,
          multiplier: parseFloat(m.multiplier)
        };
      });
    }

    if (experienceMultipliers.data) {
      experienceMultipliers.data.forEach(m => {
        config.experienceMultipliers[m.experience_level_key] = {
          key: m.experience_level_key,
          label: m.experience_label,
          multiplier: parseFloat(m.multiplier)
        };
      });
    }

    if (equipmentMultipliers.data) {
      equipmentMultipliers.data.forEach(m => {
        config.equipmentMultipliers[m.equipment_key] = {
          key: m.equipment_key,
          label: m.equipment_label,
          multiplier: parseFloat(m.multiplier)
        };
      });
    }

    if (typeMultipliers.data) {
      typeMultipliers.data.forEach(m => {
        config.typeMultipliers[m.type_key] = {
          key: m.type_key,
          label: m.type_label,
          multiplier: parseFloat(m.multiplier)
        };
      });
    }

    if (shootingMethodMultipliers.data) {
      shootingMethodMultipliers.data.forEach(m => {
        config.shootingMethodMultipliers[m.method_key] = {
          key: m.method_key,
          label: m.method_label,
          multiplier: parseFloat(m.multiplier)
        };
      });
    }

    if (hoursMultipliers.data) {
      hoursMultipliers.data.forEach(m => {
        config.hoursMultipliers[m.hours] = {
          hours: m.hours,
          label: m.hours_label,
          multiplier: parseFloat(m.multiplier)
        };
      });
    }

    // Cache config
    pricingConfigCache = config;
    cacheTimestamp = now;

    return config;
  } catch (error) {
    console.error('Error fetching pricing config:', error);
    // Return default config on error
    return {
      settings: {
        base_hour_price: 200,
        vat_percent: 15,
        is_enabled: true
      },
      cityMultipliers: {},
      experienceMultipliers: {},
      equipmentMultipliers: {},
      typeMultipliers: {},
      shootingMethodMultipliers: {},
      hoursMultipliers: {},
      workloadMultiplierByBucket: {},
      productPhysicalMultiplierByBucket: {},
      placeTypeMultiplierByKey: {},
      deliverablesSettings: {
        photoBasePrice: 20,
        videoBasePrice: 100,
        shortVideoBasePrice: 50,
        photoBulkDiscounts: [
          { minCount: 20, discount: 12 },
          { minCount: 50, discount: 15 },
        ],
        videoBulkDiscounts: [
          { minCount: 3, discount: 15 },
          { minCount: 5, discount: 40 },
        ],
        shortVideoBulkDiscounts: [],
      }
    };
  }
};

/**
 * Extract hours from duration string (e.g., "2 ساعة" -> 2)
 * This is a utility function for parsing Arabic duration strings
 */
const extractHours = (duration) => {
  if (!duration) return 1;
  
  // Try to extract number from Arabic duration strings
  const match = duration.match(/(\d+)/);
  if (match) {
    return parseInt(match[1]);
  }
  
  // Fallback mappings
  const durationMap = {
    'ساعة': 1,
    'ساعتان': 2,
    'ثلاث ساعات': 3,
    'أربع ساعات': 4,
    'خمس ساعات': 5,
    'ست ساعات': 6
  };
  
  for (const [key, hours] of Object.entries(durationMap)) {
    if (duration.includes(key)) {
      return hours;
    }
  }
  
  return 1; // Default to 1 hour
};

/**
 * Calculate final price based on booking draft and pricing config
 * 
 * This function:
 * 1. Extracts data from booking draft
 * 2. Gets multipliers from pricing config
 * 3. Uses photographer's experience_level if provided (admin-controlled), otherwise uses booking draft's experienceLevel
 * 4. Calls the pure pricing engine
 * 5. Returns formatted result with labels
 * 
 * @param {Object} bookingDraft - Booking wizard answers
 * @param {Object} pricingConfig - Pricing configuration (from getPricingConfig)
 * @param {string} photographerExperienceLevel - Optional: photographer's admin-controlled experience_level (overrides booking draft)
 * @returns {Object} Price breakdown with labels and metadata
 */
export const calculateFinalPrice = (bookingDraft, pricingConfig = null, photographerExperienceLevel = null) => {
  // Default config if not provided
  const defaultConfig = {
    settings: {
      base_hour_price: 200,
      vat_percent: 15,
      is_enabled: true
    },
    cityMultipliers: {},
    experienceMultipliers: {},
    equipmentMultipliers: {},
    typeMultipliers: {},
    shootingMethodMultipliers: {},
    hoursMultipliers: {},
    workloadMultiplierByBucket: {},
    productPhysicalMultiplierByBucket: {},
    placeTypeMultiplierByKey: {},
    deliverablesSettings: {
      photoBasePrice: 20,
      videoBasePrice: 100,
      shortVideoBasePrice: 50,
      photoBulkDiscounts: [
        { minCount: 20, discount: 12 },
        { minCount: 50, discount: 15 },
      ],
      videoBulkDiscounts: [
        { minCount: 3, discount: 15 },
        { minCount: 5, discount: 40 },
      ],
      shortVideoBulkDiscounts: [],
    }
  };

  const config = pricingConfig || defaultConfig;
  const {
    settings,
    cityMultipliers,
    experienceMultipliers,
    equipmentMultipliers,
    typeMultipliers,
    shootingMethodMultipliers,
    hoursMultipliers,
    workloadMultiplierByBucket = {},
    productPhysicalMultiplierByBucket = {},
    workloadLabelByBucket = {},
    placeTypeMultiplierByKey = {},
    deliverablesSettings,
  } = config;

  // Extract values from booking draft
  const rawCity = bookingDraft.city || '';
  // Normalize DB keys (expert) and UI keys (elite) to pricing keys (certified/distinguished/elite)
  const rawExperienceLevel = photographerExperienceLevel || bookingDraft.experienceLevel || '';
  const experienceLevel = normalizeExperienceLevelToUi(rawExperienceLevel) || rawExperienceLevel || '';
  const equipment = Array.isArray(bookingDraft.equipment) ? bookingDraft.equipment : [];
  const photographyType = (bookingDraft.photographyType || bookingDraft.serviceType || '').trim();
  const hours = extractHours(bookingDraft.duration);
  const workloadBucket = bookingDraft.workloadBucket || '';
  const productPhysicalSizeBucket =
    photographyType === 'product' ? (bookingDraft.productPhysicalSizeBucket || '') : '';

  // SAFE MODE: Normalize city for pricing lookup
  // This handles both Arabic names (الرياض) and English keys (riyadh)
  const { cityKey: normalizedCityKey, cityLabel: normalizedCityLabel, matched: cityMatched } = normalizeCity(rawCity, bookingDraft.region);
  const city = normalizedCityKey || rawCity; // Use normalized key, fallback to raw
  
  console.log('💰 City normalization for pricing:', {
    rawCity,
    normalizedCityKey,
    cityMatched,
    region: bookingDraft.region,
    usingKey: city
  });

  // Resolve hours multiplier from hours
  const hoursMultiplier = hoursMultipliers[hours]?.multiplier || 1.0;
  const hoursLabel = hoursMultipliers[hours]?.label || `${hours} ساعة`;

  // Get multipliers and labels (default to 1.0 if not found)
  const cityMultiplier = cityMultipliers[city]?.multiplier || 1.0;
  const cityLabel = cityMultipliers[city]?.label || normalizedCityLabel || rawCity;

  // experience_level is the ONLY field used for pricing (from photographer profile if available)
  const experienceMultiplier = experienceMultipliers[experienceLevel]?.multiplier || 1.0;
  const experienceLabel = experienceMultipliers[experienceLevel]?.label || experienceLevel;

  // Calculate equipment multiplier (multiply all selected equipment)
  let equipmentMultiplier = 1.0;
  const equipmentBreakdown = [];
  if (equipment.length > 0) {
    equipment.forEach(eqKey => {
      const eq = equipmentMultipliers[eqKey];
      if (eq) {
        equipmentMultiplier *= eq.multiplier;
        equipmentBreakdown.push({
          key: eq.key,
          label: eq.label,
          multiplier: eq.multiplier
        });
      }
    });
  }

  const typeMultiplier = typeMultipliers[photographyType]?.multiplier || 1.0;
  const typeLabel = typeMultipliers[photographyType]?.label || photographyType;

  // Resolve shooting method multiplier — supports multi-select (product of all chosen methods)
  const shootingMethodsArr = Array.isArray(bookingDraft.shootingMethods) && bookingDraft.shootingMethods.length > 0
    ? bookingDraft.shootingMethods
    : bookingDraft.shootingMethod ? [bookingDraft.shootingMethod] : [];
  const shootingMethod = shootingMethodsArr[0] || ''; // keep for backwards-compat
  let shootingMethodMultiplier = 1.0;
  const shootingMethodBreakdown = [];
  shootingMethodsArr.forEach(key => {
    const m = shootingMethodMultipliers[key];
    if (m) {
      shootingMethodMultiplier *= m.multiplier;
      shootingMethodBreakdown.push({ key, label: m.label, multiplier: m.multiplier });
    }
  });
  const shootingMethodLabel = shootingMethodBreakdown.map(m => m.label).join(' + ') || shootingMethod;

  // Resolve shooting place type multiplier
  const shootingPlaceType = bookingDraft.shootingPlaceType || '';
  const placeTypeEntry = placeTypeMultiplierByKey[shootingPlaceType];
  const placeTypeMultiplier = placeTypeEntry?.multiplier || 1.0;
  const placeTypeLabel = placeTypeEntry?.label || shootingPlaceType;

  const workloadPricingMaps = {
    workloadByBucket: workloadMultiplierByBucket,
    productPhysicalByBucket: productPhysicalMultiplierByBucket,
  };
  const workloadCountMultiplier = resolveWorkloadMultiplier(workloadBucket, workloadMultiplierByBucket);
  const productSizeMultiplier =
    photographyType === 'product' && productPhysicalSizeBucket
      ? resolveProductPhysicalSizeMultiplier(productPhysicalSizeBucket, productPhysicalMultiplierByBucket)
      : 1.0;
  const workloadMultiplier = resolveEffectiveWorkloadMultiplier(
    photographyType,
    workloadBucket,
    productPhysicalSizeBucket,
    workloadPricingMaps
  );

  // Calculate deliverables cost (photos and videos)
  const rawDeliverableMode = bookingDraft.deliverableMode;
  const deliverableMode = rawDeliverableMode === 'custom' ? 'custom' : (rawDeliverableMode === 'unspecified' ? 'unspecified' : 'custom');
  const rawPhotosCount = bookingDraft.photosCount;
  const rawVideosCount = bookingDraft.videosCount;
  const rawShortVideosCount = bookingDraft.shortVideosCount;
  const photosCount = (rawPhotosCount !== null && rawPhotosCount !== undefined && !isNaN(Number(rawPhotosCount))) ? Number(rawPhotosCount) : 10;
  const videosCount = (rawVideosCount !== null && rawVideosCount !== undefined && !isNaN(Number(rawVideosCount))) ? Number(rawVideosCount) : 0;
  const shortVideosCount = (rawShortVideosCount !== null && rawShortVideosCount !== undefined && !isNaN(Number(rawShortVideosCount))) ? Number(rawShortVideosCount) : 0;

  let deliverablesCost = 0;
  let photosCost = 0;
  let videosCost = 0;
  let shortVideosCost = 0;
  let photosDiscount = 0;
  let videosDiscount = 0;
  let shortVideosDiscount = 0;

  // Only calculate if mode is 'custom' and counts are specified
  if (deliverableMode === 'custom' && deliverablesSettings) {
    const photoBasePrice = Number(deliverablesSettings.photoBasePrice) || 20;
    const videoBasePrice = Number(deliverablesSettings.videoBasePrice) || 100;
    const shortVideoBasePrice = Number(deliverablesSettings.shortVideoBasePrice) || 50;

    // Photos cost with bulk discount
    if (photosCount > 0) {
      photosCost = photosCount * photoBasePrice;
      const sortedPhotoDiscounts = [...(deliverablesSettings.photoBulkDiscounts || [])].sort((a, b) => b.minCount - a.minCount);
      for (const discount of sortedPhotoDiscounts) {
        if (photosCount >= discount.minCount) {
          photosDiscount = discount.discount;
          photosCost = photosCost * (1 - discount.discount / 100);
          break;
        }
      }
    }

    // Long videos cost with bulk discount
    if (videosCount > 0) {
      videosCost = videosCount * videoBasePrice;
      const sortedVideoDiscounts = [...(deliverablesSettings.videoBulkDiscounts || [])].sort((a, b) => b.minCount - a.minCount);
      for (const discount of sortedVideoDiscounts) {
        if (videosCount >= discount.minCount) {
          videosDiscount = discount.discount;
          videosCost = videosCost * (1 - discount.discount / 100);
          break;
        }
      }
    }

    // Short videos cost with bulk discount
    if (shortVideosCount > 0) {
      shortVideosCost = shortVideosCount * shortVideoBasePrice;
      const sortedShortDiscounts = [...(deliverablesSettings.shortVideoBulkDiscounts || [])].sort((a, b) => b.minCount - a.minCount);
      for (const discount of sortedShortDiscounts) {
        if (shortVideosCount >= discount.minCount) {
          shortVideosDiscount = discount.discount;
          shortVideosCost = shortVideosCost * (1 - discount.discount / 100);
          break;
        }
      }
    }

    deliverablesCost = photosCost + videosCost + shortVideosCost;
  }

  // Call the pure pricing engine (workloadMultiplier is applied below in pricingService,
  // not here, to correctly include deliverables before multiplying)
  const priceResult = calculatePrice({
    baseHourPrice: settings.base_hour_price,
    hours,
    hoursMultiplier,
    cityMultiplier,
    experienceMultiplier,
    equipmentMultiplier,
    typeMultiplier,
    shootingMethodMultiplier,
    placeTypeMultiplier,
    workloadMultiplier: 1,
    vatPercent: settings.vat_percent,
    isEnabled: settings.is_enabled
  });

  // Enhance breakdown with labels
  const enhancedBreakdown = priceResult.breakdown.map(item => {
    if (item.type === 'city' && cityLabel) {
      return { ...item, label: `مضاعف المدينة (${cityLabel})` };
    }
    if (item.type === 'experience' && experienceLabel) {
      return { ...item, label: `مضاعف الخبرة (${experienceLabel})` };
    }
    if (item.type === 'equipment' && equipmentBreakdown.length > 0) {
      // Replace generic equipment breakdown with individual items
      return null; // Will be replaced below
    }
    if (item.type === 'type' && typeLabel) {
      return { ...item, label: `مضاعف نوع التصوير (${typeLabel})` };
    }
    if (item.type === 'shootingMethod') {
      // Replace with null when multiple methods selected — added individually below
      if (shootingMethodBreakdown.length > 1) return null;
      if (shootingMethodBreakdown.length === 1) {
        return { ...item, label: `مضاعف وسيلة التصوير (${shootingMethodBreakdown[0].label})` };
      }
      return item;
    }
    if (item.type === 'hours' && hoursLabel) {
      return { ...item, label: `مضاعف المدة (${hoursLabel})`, multiplier: hoursMultiplier };
    }
    if (item.type === 'placeType' && placeTypeLabel) {
      return { ...item, label: `مضاعف مكان التصوير (${placeTypeLabel})` };
    }
    return item;
  }).filter(Boolean);

  // Add individual equipment items to breakdown
  if (equipmentMultiplier !== 1.0 && equipmentBreakdown.length > 0) {
    equipmentBreakdown.forEach(eq => {
      enhancedBreakdown.push({
        label: `مضاعف المعدات (${eq.label})`,
        value: eq.multiplier,
        multiplier: eq.multiplier,
        type: 'equipment'
      });
    });
  }

  // Add individual shooting method items when multiple are selected
  if (shootingMethodBreakdown.length > 1) {
    shootingMethodBreakdown.forEach(m => {
      enhancedBreakdown.push({
        label: `مضاعف وسيلة التصوير (${m.label})`,
        value: m.multiplier,
        multiplier: m.multiplier,
        type: 'shootingMethod'
      });
    });
  }

  // Add deliverables to breakdown if applicable
  if (deliverablesCost > 0) {
    if (photosCost > 0) {
      enhancedBreakdown.push({
        label: `تسعير الصور (${photosCount} صورة${photosDiscount > 0 ? ` - خصم ${photosDiscount}%` : ''})`,
        value: Math.round(photosCost),
        type: 'photos',
        isAddition: true
      });
    }
    if (videosCost > 0) {
      enhancedBreakdown.push({
        label: `فيديو طويل (${videosCount}${videosDiscount > 0 ? ` - خصم ${videosDiscount}%` : ''})`,
        value: Math.round(videosCost),
        type: 'videos',
        isAddition: true
      });
    }
    if (shortVideosCost > 0) {
      enhancedBreakdown.push({
        label: `فيديو قصير (${shortVideosCount}${shortVideosDiscount > 0 ? ` - خصم ${shortVideosDiscount}%` : ''})`,
        value: Math.round(shortVideosCost),
        type: 'shortVideos',
        isAddition: true
      });
    }
  }

  // Add workload multiplier to breakdown (applied at final stage)
  if (workloadMultiplier !== 1.0) {
    const workloadBucketLabel = workloadLabelByBucket[workloadBucket] || workloadBucket;
    enhancedBreakdown.push({
      label: `مضاعف حجم العمل (${workloadBucketLabel})`,
      value: workloadMultiplier,
      multiplier: workloadMultiplier,
      type: 'workload'
    });
  }

  // Apply workload to session cost ONLY (photographer time), then add deliverables, then VAT.
  // Workload affects how much work the photographer does — not the per-photo/video price.
  const sessionCostWithWorkload = Math.round(priceResult.subtotal * workloadMultiplier);
  const subtotalWithDeliverables = sessionCostWithWorkload + deliverablesCost;
  const vatWithDeliverables = Math.round(subtotalWithDeliverables * (settings.vat_percent / 100));
  const totalWithDeliverables = subtotalWithDeliverables + vatWithDeliverables;

  // Return result with metadata
  return {
    ...priceResult,
    // Override totals to include deliverables
    subtotal: Math.round(subtotalWithDeliverables),
    vat: vatWithDeliverables,
    vatAmount: vatWithDeliverables, // Also override vatAmount for compatibility
    total: totalWithDeliverables,
    totalPrice: totalWithDeliverables, // Also set totalPrice for checkout compatibility
    // Deliverables info
    deliverables: {
      mode: deliverableMode,
      photosCount,
      videosCount,
      shortVideosCount,
      photosCost: Math.round(photosCost),
      videosCost: Math.round(videosCost),
      shortVideosCost: Math.round(shortVideosCost),
      photosDiscount,
      videosDiscount,
      shortVideosDiscount,
      totalCost: Math.round(deliverablesCost)
    },
    multipliers: {
      city: { key: city, label: cityLabel, multiplier: cityMultiplier },
      experience: { key: experienceLevel, label: experienceLabel, multiplier: experienceMultiplier },
      equipment: equipmentBreakdown,
      type: { key: photographyType, label: typeLabel, multiplier: typeMultiplier },
      shootingMethod: { key: shootingMethod, label: shootingMethodLabel, multiplier: shootingMethodMultiplier, methods: shootingMethodBreakdown },
      placeType: { key: shootingPlaceType, label: placeTypeLabel, multiplier: placeTypeMultiplier },
      hours: { hours, label: hoursLabel, multiplier: hoursMultiplier },
      workload: {
        key: workloadBucket,
        multiplier: workloadMultiplier,
        countMultiplier: workloadCountMultiplier,
        productPhysicalSizeKey: productPhysicalSizeBucket || null,
        productSizeMultiplier:
          photographyType === 'product' && productPhysicalSizeBucket ? productSizeMultiplier : null,
      },
    },
    breakdown: enhancedBreakdown,
    workloadBucket,
    workloadMultiplier,
    productPhysicalSizeBucket: productPhysicalSizeBucket || null,
  };
};

export const pricingService = {
  getPricingConfig,
  calculateFinalPrice,
  clearPricingCache
};
